import { Button, Stack, Text } from '@chakra-ui/react'
import { useMutation } from '@tanstack/react-query'
import { Link as RouterLink, useNavigate } from 'react-router'
import { api, isApiError } from '~/api/client'
import { useFetchRegistration } from '~/api/hooks'
import { ActionBar } from '~/components/member/ActionBar'
import { ErrorAlert, useFriendlyError } from '~/components/member/feedback'
import { ArtHeading } from '~/components/member/Illustration'
import { MemberLayout } from '~/components/member/layout'
import { useT } from '~/i18n/use-t'
import { REGISTRATION_ENDED_STATE } from '~/lib/claim'
import { pathForStatus } from '~/lib/flow'
import { useHashParams } from '~/lib/hash'

/**
 * The page behind "Continue my registration" in the emails (`/join/continue#t=…`). The token stays in the fragment
 * and is posted only when the button is pressed (R23). The answer sets the registration cookie; the page then goes
 * to the step the server says.
 */
export default function Continue() {
  const { t } = useT()
  const friendly = useFriendlyError()
  const navigate = useNavigate()
  const params = useHashParams()
  const refetchRegistration = useFetchRegistration()
  const token = params.get('t') ?? params.get('token')

  const go = useMutation({
    mutationFn: async () => {
      const res = await api('registrationContinue', { body: { token: token! } })
      if (res.next === 'login') return null
      return refetchRegistration()
    },
    // A decided registration (`login`): the sign-in page, which says the registration is finished and offers the
    // sign-in email (passwordless spec §2).
    onSuccess: (reg) => (reg ? navigate(pathForStatus(reg)) : navigate('/login', { state: REGISTRATION_ENDED_STATE })),
  })

  const badLink = !token || (go.isError && isApiError(go.error) && go.error.status < 500)
  return (
    <MemberLayout>
      <Stack gap={6}>
        <ArtHeading title={t('continuePage.title')} art='about' />
        {badLink ? (
          <>
            <Text role='alert'>{t('continuePage.oldLink')}</Text>
            <ActionBar>
              <Button asChild size='lg' w='full'>
                <RouterLink to='/join'>{t('confirm.goBack')}</RouterLink>
              </Button>
            </ActionBar>
          </>
        ) : (
          <>
            <Text>{t('continuePage.text')}</Text>
            {go.isError && <ErrorAlert>{friendly(go.error)}</ErrorAlert>}
            <ActionBar>
              <Button size='lg' w='full' loading={go.isPending} onClick={() => go.mutate()}>
                {t('continuePage.button')}
              </Button>
            </ActionBar>
          </>
        )}
      </Stack>
    </MemberLayout>
  )
}
